import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../../constants';
import { useThemeContext } from '../../../../../../global-state/themeContext';
import { QueueLengthPerformanceItem, QueueLengthTillData, TillType } from '../../../../../../hooks';
import { Typography } from '../../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { DynamicTab } from '../../../../../../stories/molecules';
import { Colors, Typographies } from '../../../../../../theme';
import { homeTranslations } from '../../../../../../translations';
import { getFormattedTimeUnit, Spacing } from '../../../../../../utils';
import { getAveragePercent, getTillAveragePercent } from '../util';

const GridCellContainer: FC<PropsWithChildren<{ gridColor?: Colors }>> = ({ children, gridColor }) => {
  return (
    <RowFlexContainer
      flex={1}
      height={[7]}
      justifyContent="center"
      alignItems="center"
      backgroundColor={gridColor ? gridColor : 'inherit'}
      margin={[0, 0, 1]}
    >
      {children}
    </RowFlexContainer>
  );
};

export type TillAggregateData = {
  tillId: string;
  tillType: TillType;
  values: {
    [key: string]: { queueCount: number; transactions: number } | undefined;
  };
};

export const GridView = ({
  performanceItems,
  filteredPerformanceItems,
  target,
  interval,
}: {
  performanceItems: QueueLengthPerformanceItem[];
  filteredPerformanceItems: (QueueLengthPerformanceItem | null)[];
  target: number | null;
  interval: StorePerformanceInterval;
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const { queueLength: queueLengthTranslations } = homeTranslations;

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  const tillAggregateDataMap: Map<string, TillAggregateData> = new Map();

  filteredPerformanceItems.forEach((item) => {
    if (!item) return;

    const { timeUnit, tillData } = item;

    tillData.forEach((data) => {
      const { tillId, tillType, queueCount, transactions } = data;

      // Filter based on till type
      if (tabIndex === 1 && tillType != 'DEFAULT' && tillType != 'null') return;

      if (tabIndex === 2 && tillType != 'SELFCHECKOUT' && tillType != 'SCO-MP') return;

      const tillAggregateData = tillAggregateDataMap.get(tillId);

      tillAggregateDataMap.set(tillId, {
        tillId,
        tillType,
        values: !tillAggregateData
          ? {
              [timeUnit]: { queueCount, transactions },
            }
          : {
              ...tillAggregateData.values,
              [timeUnit]: { queueCount, transactions },
            },
      });
    });
  });

  const getGridCellColor = ({
    value,
    targetValue,
    isOddRow,
    isHeader = false,
  }: {
    value: number | null;
    targetValue: number | null;
    isOddRow: boolean;
    isHeader?: boolean;
  }): Colors => {
    const defaultColor = isHeader
      ? mode !== 'dark'
        ? 'baseGray'
        : 'backgroundDark'
      : isOddRow
      ? 'backgroundSelectedDark'
      : 'backgroundSelectedHover';

    if (value === null || targetValue === null) return defaultColor;

    const percentProgress = (value / targetValue) * 100;

    if (percentProgress <= 97) return 'successTransparent';
    if (percentProgress <= 100) return 'successLightTransparent';
    if (percentProgress <= 103) return 'warningTransparent';
    return 'errorTransparent';
  };

  const tillAggregateDataList = Array.from(tillAggregateDataMap.values());

  const hasDefaultTill = !!filteredPerformanceItems.find((filteredPerformanceItem) => {
    if (filteredPerformanceItem === null) return false;

    const { tillData } = filteredPerformanceItem;

    const defaultQueueLengthTillData = tillData.find(
      (tillDataItem) => tillDataItem.tillType === 'DEFAULT' || tillDataItem.tillType === 'null',
    );

    return !!defaultQueueLengthTillData;
  });

  const hasScoTill = !!filteredPerformanceItems.find((filteredPerformanceItem) => {
    if (filteredPerformanceItem === null) return false;

    const { tillData } = filteredPerformanceItem;

    const scoQueueLengthTillData = tillData.find(
      (tillDataItem) => tillDataItem.tillType === 'SELFCHECKOUT' || tillDataItem.tillType === 'SCO-MP',
    );

    return !!scoQueueLengthTillData;
  });

  return (
    <ColumnFlexContainer flex={1} minHeight={[0]} data-testid="grid-view-desktop">
      <DynamicTab
        tabLabelArray={[
          {
            tabName: 'All',
            isTabVisible: true,
            isTabDisabled: false,
            labelType: 'body3' as Typographies,
          },
          {
            tabName: t(queueLengthTranslations.tabDefault.key, queueLengthTranslations.tabDefault.defaultValue),
            isTabVisible: true,
            isTabDisabled: !hasDefaultTill,
            labelType: 'body3' as Typographies,
          },
          {
            tabName: t(
              queueLengthTranslations.tabSelfCheckout.key,
              queueLengthTranslations.tabSelfCheckout.defaultValue,
            ),
            isTabVisible: true,
            isTabDisabled: !hasScoTill,
            labelType: 'body3' as Typographies,
            labelMargin: [0] as Spacing,
          },
        ]}
        handleTabChange={handleTabChange}
        tabValue={tabIndex}
        variant="scrollable"
        tabBgColor={mode === 'light' ? 'baseGray' : 'backgroundTableHeaderDark'}
        content={[]}
      />

      <ColumnFlexContainer padding={[4, 4, 4, 2]} overflow="scroll" data-testid="grid-container">
        <RowFlexContainer gap={[1]} key="Avg">
          <GridCellContainer>
            <Typography type="button">Avg</Typography>
          </GridCellContainer>

          {performanceItems.map((_, index) => {
            const filteredPerformanceItem = filteredPerformanceItems[index];

            if (!filteredPerformanceItem) {
              return (
                <GridCellContainer
                  key={`header-${index}`}
                  gridColor={getGridCellColor({ value: null, targetValue: null, isOddRow: false, isHeader: true })}
                >
                  <Typography type="body3">{'-'}</Typography>
                </GridCellContainer>
              );
            }

            const { tillData } = filteredPerformanceItem;

            let filteredQueueLengthTillData: QueueLengthTillData[] = tillData;

            // Filter based on till type
            if (tabIndex === 1) {
              filteredQueueLengthTillData = tillData.filter(
                (item) => item.tillType === 'DEFAULT' || item.tillType === null,
              );
            }

            if (tabIndex === 2) {
              filteredQueueLengthTillData = tillData.filter((item) => item.tillType === 'SELFCHECKOUT');
            }

            const totalQueueCount = filteredQueueLengthTillData.reduce((prev, curr) => {
              return prev + curr.queueCount;
            }, 0);

            const totalTransactions = filteredQueueLengthTillData.reduce((prev, curr) => {
              return prev + curr.transactions;
            }, 0);

            const percentValue = (totalTransactions === 0 ? 0 : totalQueueCount / totalTransactions) * 100;

            return (
              <GridCellContainer
                key={`header-${index}`}
                gridColor={getGridCellColor({
                  value: percentValue,
                  targetValue: target,
                  isOddRow: false,
                  isHeader: true,
                })}
              >
                <Typography type="body3">{percentValue.toFixed(0)}%</Typography>
              </GridCellContainer>
            );
          })}

          <GridCellContainer
            gridColor={getGridCellColor({
              value: getAveragePercent(tillAggregateDataList),
              targetValue: target,
              isOddRow: false,
            })}
          >
            <Typography type="body3">{getAveragePercent(tillAggregateDataList).toFixed(0)}%</Typography>
          </GridCellContainer>
        </RowFlexContainer>

        <ContainerV2 overflow="scroll" data-testid="InnerGridContainer">
          {tillAggregateDataList.map((tillAggregateData, index) => {
            const { tillId, values } = tillAggregateData;
            const isOddRow = index % 2 != 0;

            return (
              <RowFlexContainer gap={[1]} key={`row-${tillId}`}>
                <GridCellContainer>
                  <Typography>{tillId}</Typography>
                </GridCellContainer>

                {performanceItems.map((performanceItem) => {
                  const { timeUnit } = performanceItem;

                  const timeUnitValues = values[timeUnit];

                  if (!timeUnitValues) {
                    return (
                      <GridCellContainer
                        key={timeUnit}
                        gridColor={getGridCellColor({ value: null, targetValue: null, isOddRow: isOddRow })}
                      >
                        <Typography>{'-'}</Typography>
                      </GridCellContainer>
                    );
                  }

                  const percentValue =
                    (timeUnitValues.transactions === 0
                      ? 0
                      : timeUnitValues.queueCount / timeUnitValues.transactions) * 100;

                  return (
                    <GridCellContainer
                      key={timeUnit}
                      gridColor={getGridCellColor({
                        value: percentValue,
                        targetValue: target,
                        isOddRow: isOddRow,
                      })}
                    >
                      <Typography>{percentValue.toFixed(0)}%</Typography>
                    </GridCellContainer>
                  );
                })}

                <GridCellContainer
                  gridColor={getGridCellColor({
                    value: getTillAveragePercent(tillAggregateData),
                    targetValue: target,
                    isOddRow: isOddRow,
                  })}
                >
                  <Typography type="body3">{getTillAveragePercent(tillAggregateData).toFixed(0)}%</Typography>
                </GridCellContainer>
              </RowFlexContainer>
            );
          })}
        </ContainerV2>

        <RowFlexContainer gap={[1]}>
          <GridCellContainer>
            <Typography>
              {t(queueLengthTranslations.tillVsTime.key, queueLengthTranslations.tillVsTime.defaultValue)}
            </Typography>
          </GridCellContainer>

          {performanceItems.map((performanceItem) => {
            const { timeUnit } = performanceItem;

            return (
              <GridCellContainer key={timeUnit}>
                <Typography>{getFormattedTimeUnit(timeUnit, interval)}</Typography>
              </GridCellContainer>
            );
          })}

          <GridCellContainer>
            <Typography type="body3">Avg</Typography>
          </GridCellContainer>
        </RowFlexContainer>
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};
